import React, { useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Dropdown, Icon } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { DateInput, RenderFields, RestrictedBanner } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { ReportType, ReportingRepository } from '~/repositories';
import { NotificationPropsType, useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { EditToolbar, Inner, Paragraph } from '~/styles';
import { getDateFormat } from '~/util';

const StyledParagraph = styled(Paragraph)`
  max-width: 64ch;
  margin: 0 0 16px 0;
`;

type DateRangePreset = 'today' | 'last3Days' | 'lastWeek' | 'lastMonth' | 'custom';

interface ScheduleReportForm {
  type: ReportType | undefined;
  dateRangePreset: DateRangePreset;
  fromDate: string | undefined;
  toDate: string | undefined;
}

const getPresetDates = (preset: Exclude<DateRangePreset, 'custom'>): { fromDate: string; toDate: string } => {
  const today = new Date();
  const from = new Date();

  if (preset === 'last3Days') {
    from.setDate(from.getDate() - 3);
  } else if (preset === 'lastWeek') {
    from.setDate(from.getDate() - 7);
  } else if (preset === 'lastMonth') {
    from.setMonth(from.getMonth() - 1);
  }

  return { fromDate: getDateFormat(from), toDate: getDateFormat(today) };
};

const todayFormatted = getDateFormat(new Date());

export const PageReportingSchedule: React.FC = () => {
  const currentPermission = useSessionStore((s) => s.currentPermission);
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const formMethods = useForm<ScheduleReportForm>({
    defaultValues: {
      type: undefined,
      dateRangePreset: 'today',
      fromDate: todayFormatted,
      toDate: todayFormatted,
    },
  });

  const { handleSubmit, setValue } = formMethods;

  const handlePresetChange = (preset: DateRangePreset) => {
    setValue('dateRangePreset', preset);

    if (preset !== 'custom') {
      const { fromDate, toDate } = getPresetDates(preset);
      setValue('fromDate', fromDate);
      setValue('toDate', toDate);
    }
  };

  const handleValidationError = (errors: FieldErrors<ScheduleReportForm>) => {
    if (errors.type) {
      addDangerNotification({ content: 'Please select a type for this report' });
    } else if (errors.fromDate || errors.toDate) {
      addDangerNotification({ content: 'Please select a start date and end date' });
    }
  };

  const handleSchedule = (data: ScheduleReportForm) => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    ReportingRepository.schedule({
      fromDate: data.fromDate,
      toDate: data.toDate,
      type: data.type,
    })
      .then(() => {
        addSuccessNotification({
          content: 'Report scheduled',
        });
        navigate(ROUTE.REPORTING);
      })
      .catch((error) => {
        const object: NotificationPropsType = {
          content: error.message,
        };

        if (error.code === 'invalid_field_value') {
          object.timeout = 8000;
          object.actionButton = {
            label: 'Platform Settings',
            onClick: () => navigate(ROUTE.PLATFORM_SETTINGS),
          };
        }

        addDangerNotification(object);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <PageActions>
        <Button onClick={() => navigate(ROUTE.REPORTING)} variant="secondary" size="small">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(handleSchedule, handleValidationError)}
          isLoading={isLoading}
          isDisabled={currentPermission?.reporting !== 'write'}
          size="small"
          icon={<Icon.Plus />}
        >
          Schedule Report
        </Button>
      </PageActions>

      {currentPermission?.reporting !== 'write' && (
        <Inner pb={0}>
          <RestrictedBanner />
        </Inner>
      )}

      <Inner pb={0} data-disabled={currentPermission?.reporting !== 'write'}>
        <StyledParagraph>
          Create a report for a date range or single day. Reports that include the current business day will be
          completed at the end of the day.
        </StyledParagraph>
      </Inner>

      <FormProvider {...formMethods}>
        <form
          autoComplete="off"
          onSubmit={handleSubmit(handleSchedule, handleValidationError)}
          data-disabled={currentPermission?.reporting !== 'write'}
        >
          {RenderFields<ScheduleReportForm>({
            sections: [
              {
                fields: [
                  {
                    id: 'type',
                    label: 'Type',
                    rules: { required: true },
                    children: ({ value, onChange }, { isTouched, error }) => (
                      <Dropdown
                        options={[
                          {
                            label: 'Bank account summary',
                            value: 'bank_account_summary',
                          },
                          {
                            label: 'Bank account transaction',
                            value: 'bank_account_transaction',
                          },
                        ]}
                        active={value}
                        fullWidth
                        onChange={onChange}
                        hasError={isTouched && !!error}
                      />
                    ),
                  },
                  {
                    id: 'dateRangePreset',
                    label: 'Date Range Preset',
                    children: ({ value }) => (
                      <Dropdown
                        options={[
                          { label: 'Today', value: 'today' },
                          { label: 'Last 3 Days', value: 'last3Days' },
                          { label: 'Last Week', value: 'lastWeek' },
                          { label: 'Last Month', value: 'lastMonth' },
                        ]}
                        active={value}
                        fullWidth
                        onChange={handlePresetChange}
                        placeholder="Custom"
                      />
                    ),
                  },
                  {
                    id: 'fromDate',
                    label: 'Start Date',
                    rules: { required: true },
                    children: ({ value, onChange }) => (
                      <DateInput
                        value={value ? String(value) : undefined}
                        onChange={(newValue) => {
                          onChange(newValue);
                          setValue('dateRangePreset', 'custom');
                        }}
                        disableBefore={new Date('2022-01-01')}
                        disableAfter={new Date()}
                      />
                    ),
                  },
                  {
                    id: 'toDate',
                    label: 'End Date',
                    rules: { required: true },
                    children: ({ value, onChange }) => (
                      <DateInput
                        value={value ? String(value) : undefined}
                        onChange={(newValue) => {
                          onChange(newValue);
                          setValue('dateRangePreset', 'custom');
                        }}
                        disableBefore={new Date('2022-01-01')}
                        disableAfter={new Date()}
                      />
                    ),
                  },
                ],
              },
            ],
          })}
        </form>
      </FormProvider>

      <Inner>
        <EditToolbar>
          <Button onClick={() => navigate(ROUTE.REPORTING)} variant="secondary" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(handleSchedule, handleValidationError)}
            isLoading={isLoading}
            isDisabled={currentPermission?.reporting !== 'write'}
            size="small"
            icon={<Icon.Plus />}
          >
            Schedule Report
          </Button>
        </EditToolbar>
      </Inner>
    </>
  );
};
